import React from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { Linkedin, Award, Briefcase, Code, Palette, Smartphone } from 'lucide-react';

const mentors = [
    {
        name: 'Aditya Sharma',
        role: 'Lead UI/UX Designer',
        initials: 'AS',
        experience: '6+ years',
        skills: ['Figma', 'User Research', 'Design Systems'],
        bio: 'Designed apps for 30+ clients. Ex-Infosys. Trained 500+ students.',
        projects: ['E-commerce platforms', 'Fintech dashboards', 'Healthcare apps'],
    },
    {
        name: 'Sneha Patel',
        role: 'Senior App Developer',
        initials: 'SP',
        experience: '5+ years',
        skills: ['Flutter', 'React Native', 'Firebase'],
        bio: 'Built 15+ apps shipped to Play Store. Open-source contributor.',
        projects: ['Food delivery apps', 'Social platforms', 'SaaS products'],
    },
    {
        name: 'Vikram Joshi',
        role: 'AI & Automation Lead',
        initials: 'VJ',
        experience: '4+ years',
        skills: ['AI Integration', 'Python', 'ChatGPT API'],
        bio: 'Specializes in AI-powered workflows. Helped 20+ startups automate.',
        projects: ['AI chatbots', 'Automated design pipelines', 'ML-powered apps'],
    },
    {
        name: 'Priya Desai',
        role: 'UX Researcher',
        initials: 'PD',
        experience: '5+ years',
        skills: ['User Testing', 'Analytics', 'Prototyping'],
        bio: 'Conducted 100+ user research sessions. Previously at Flipkart.',
        projects: ['E-commerce UX', 'Mobile app research', 'Enterprise dashboards'],
    },
    {
        name: 'Karan Mehta',
        role: 'Full-Stack Developer',
        initials: 'KM',
        experience: '7+ years',
        skills: ['React', 'Node.js', 'AWS'],
        bio: 'Full-stack expertise. Built scalable systems handling 1M+ users.',
        projects: ['SaaS platforms', 'API architectures', 'Cloud infrastructure'],
    },
    {
        name: 'Nisha Gupta',
        role: 'Motion Designer',
        initials: 'NG',
        experience: '4+ years',
        skills: ['After Effects', 'Lottie', 'Framer Motion'],
        bio: 'Creates engaging micro-interactions. Worked with 25+ brands.',
        projects: ['App animations', 'Brand motion', 'Interactive prototypes'],
    },
];

const partners = [
    'TCS', 'Infosys', 'Flipkart', 'Razorpay', 'Zerodha', 'Freshworks', 'Zoho', 'Swiggy'
];

const Mentors = () => {
    return (
        <>
            <Helmet>
                <title>Our Mentors | PyrusMedia Academy</title>
                <meta name="description" content="Learn from industry practitioners at PyrusMedia. Our mentors are working professionals who ship real client projects daily." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[120px]" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                    >
                        Learn From <span className="text-[#bebd19]">Industry Practitioners</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-gray-500 max-w-2xl mx-auto"
                    >
                        Our mentors are working professionals at PyrusMedia who ship real client projects daily.
                    </motion.p>
                </div>
            </section>

            {/* Mentors Grid */}
            <Section className="bg-gray-50">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {mentors.map((mentor, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-[#bebd19] transition-all group"
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 rounded-full bg-[#bebd19]/20 flex items-center justify-center text-2xl font-bold text-[#bebd19] shrink-0">
                                    {mentor.initials}
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#bebd19] transition-colors">{mentor.name}</h3>
                                    <p className="text-[#bebd19] font-medium text-sm">{mentor.role}</p>
                                </div>
                            </div>

                            <p className="text-gray-500 text-sm mb-4">{mentor.bio}</p>

                            <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                                <Briefcase size={14} />
                                <span>{mentor.experience}</span>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {mentor.skills.map((skill, j) => (
                                    <span key={j} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                        {skill}
                                    </span>
                                ))}
                            </div>

                            <div className="border-t border-gray-100 pt-4">
                                <p className="text-xs text-gray-400 mb-2">Notable projects:</p>
                                <div className="flex flex-wrap gap-2">
                                    {mentor.projects.map((p, j) => (
                                        <span key={j} className="text-xs text-gray-500">#{p.replace(/\s+/g, '')}</span>
                                    ))}
                                </div>
                            </div>

                            <a
                                href="#"
                                className="inline-flex items-center gap-2 mt-4 text-sm text-gray-400 hover:text-[#bebd19] transition-colors"
                            >
                                <Linkedin size={16} />
                                <span>LinkedIn Profile</span>
                            </a>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Hiring Partners */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Where Our Graduates Work</h2>
                <p className="text-gray-500 text-center mb-12 max-w-xl mx-auto">
                    Our alumni have landed roles at top tech companies across India.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                    {partners.map((partner, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.05 }}
                            className="bg-gray-50 h-20 rounded-xl border border-gray-200 flex items-center justify-center"
                        >
                            <span className="text-gray-400 font-bold text-lg">{partner}</span>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* CTA */}
            <Section className="bg-[#bebd19]">
                <div className="text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Start Learning From the Best</h2>
                    <p className="text-black/70 text-lg mb-8 max-w-xl mx-auto">
                        Our mentors don't just teach — they work on the same projects you'll learn from.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button to="/courses/ui-ux-design-with-ai" className="bg-black text-white hover:bg-gray-800">
                            UI/UX Design Course
                        </Button>
                        <Button to="/courses/app-development-with-ai" variant="outline" className="border-black text-black hover:bg-black/10">
                            App Development Course
                        </Button>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default Mentors;
